import { differenceInCalendarDays, isValid } from 'date-fns';
import { createDateSafe } from './dates';
import { isoToBrDate } from './vacationDates';

/**
 * Art. 134 §3º da CLT: as férias não podem começar no próprio feriado nem nos dois dias que o
 * antecedem.
 *
 * A mesma proibição vale para sábado e domingo (dias de repouso), mas essa parte não vive aqui: é
 * dia da semana, não data de calendário, e está em `vacationStartDateRules.ts`
 * (`BLOCKED_START_WEEKDAYS` cobre domingo, sábado e os dois dias que antecedem cada um — daí
 * quinta e sexta entrarem na lista).
 */
export const DAYS_BLOCKED_BEFORE_HOLIDAY = 2;

/**
 * O feriado que impede o início nesta data, ou `null`.
 *
 * A janela é de **0 a 2 dias** antes: `0` é o próprio feriado. Devolve o feriado, e não um
 * booleano, porque a mensagem precisa dizer **qual** data está atrapalhando — "não pode começar
 * aqui" sem o motivo manda o colaborador tentar às cegas.
 */
export function findBlockingHoliday(isoDate: string, holidays: string[] = []): string | null {
    if (holidays.length === 0) return null;

    const date = createDateSafe(isoDate);
    if (!date || !isValid(date)) return null;

    return (
        holidays.find((holiday) => {
            const holidayDate = createDateSafe(holiday);
            if (!holidayDate) return false;

            const distance = differenceInCalendarDays(holidayDate, date);
            return distance >= 0 && distance <= DAYS_BLOCKED_BEFORE_HOLIDAY;
        }) ?? null
    );
}

export function isBlockedHolidayStart(isoDate: string, holidays: string[] = []): boolean {
    return findBlockingHoliday(isoDate, holidays) !== null;
}

/**
 * A mensagem distingue começar **no** feriado de começar **na véspera**: são proibições diferentes
 * da mesma regra, e o colaborador precisa entender qual das duas o barrou para escolher outra data.
 */
export function buildHolidayBlockMessage(startIsoDate: string, holidayIsoDate: string): string {
    const start = createDateSafe(startIsoDate);
    const holiday = createDateSafe(holidayIsoDate);
    const isSameDay = start && holiday && differenceInCalendarDays(holiday, start) === 0;

    if (isSameDay) {
        return `As férias não podem começar em um feriado (${isoToBrDate(holidayIsoDate)}).`;
    }

    return `As férias não podem começar nos dois dias que antecedem um feriado (${isoToBrDate(
        holidayIsoDate
    )}).`;
}
